import { Group, Stack, Text } from "@mantine/core"
import { CertificateRequestStatus } from "@tfg/db/browser"
import { Card, Checkbox } from "@tfg/ui"
import { useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"

import {
  CertificateByOrganizationDto,
  FindCertificateRequestsByCertificateIdItem,
} from "~/gen/api"
import { formatDate } from "~/utils/date"
import { useGetOperations } from "~/utils/operations"

export type Props = {
  certificateRequest: FindCertificateRequestsByCertificateIdItem
  certificate: CertificateByOrganizationDto
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
}

export const CertificateRequestValidation = ({
  certificateRequest,
  certificate,
  checked,
  onCheckboxChange,
}: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("certificates")

  const operations = useGetOperations()

  const isCertificateCompleted = useMemo(
    () =>
      [
        CertificateRequestStatus.ACCEPTED,
        CertificateRequestStatus.REJECTED,
        CertificateRequestStatus.CANCELED,
      ].includes(certificateRequest.status),
    [certificateRequest.status]
  )

  const scopeDescriptionColor: Record<string, string | undefined> =
    useMemo(() => {
      if (isCertificateCompleted) {
        return {}
      }

      return {
        type:
          certificate.type.id === certificateRequest.type.id
            ? "var(--mantine-color-success-text)"
            : "inherit",
        maxIssueDate:
          certificate.issueDate &&
          certificateRequest.maxIssueDate &&
          certificate.expiryDate &&
          new Date(certificate.issueDate) <
            new Date(certificateRequest.maxIssueDate) &&
          new Date(certificate.expiryDate) >
            new Date(certificateRequest.maxIssueDate)
            ? "var(--mantine-color-success)"
            : "inherit",
        productionSiteName:
          certificate.productionSite.id === certificateRequest.productionSite.id
            ? "var(--mantine-color-success-text)"
            : "inherit",
        productionSiteOrganizationName:
          certificate.productionSite.id === certificateRequest.productionSite.id
            ? "var(--mantine-color-success-text)"
            : "inherit",
        activityName: "inherit",
      }
    }, [
      certificate.type.id,
      certificate.issueDate,
      certificate.expiryDate,
      certificate.productionSite.id,
      certificateRequest,
      isCertificateCompleted,
    ])

  if (isCertificateCompleted) {
    return null
  }

  return (
    <Card key={certificateRequest.id} h="fit-content" mih="fit-content">
      <Stack gap="xs">
        <Text fw={300} c="var(--mantine-color-gray-5)" ta="right">
          {t("modal.associatedRequests.card.header", {
            createdAt: formatDate(certificateRequest.createdAt, language),
          })}
        </Text>
        <Text>
          <Trans
            ns="certificates"
            i18nKey="modal.associatedRequests.card.description.common"
            components={{
              type: <strong style={{ color: scopeDescriptionColor.type }} />,
              maxIssueDate: (
                <strong style={{ color: scopeDescriptionColor.maxIssueDate }} />
              ),
              productionSiteName: (
                <strong
                  style={{ color: scopeDescriptionColor.productionSiteName }}
                />
              ),
              productionSiteOrganizationName: (
                <strong
                  style={{
                    color: scopeDescriptionColor.productionSiteOrganizationName,
                  }}
                />
              ),
              activityName: (
                <strong style={{ color: scopeDescriptionColor.activityName }} />
              ),
            }}
            values={{
              type: certificateRequest.type.name,
              maxIssueDate: certificateRequest.maxIssueDate
                ? formatDate(certificateRequest.maxIssueDate, language)
                : "",
              productionSiteName: certificateRequest.productionSite.name ?? "",
              productionSiteOrganizationName:
                certificateRequest.productionSite.organization.name ?? "",
              activityName:
                operations[certificateRequest.activityName]?.name ??
                certificateRequest.activityName,
            }}
          />
        </Text>
        <Group>
          {certificateRequest.product ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.product"
                components={{
                  productBrandReference: <strong />,
                  productColor: <strong />,
                }}
                values={{
                  productColor: certificateRequest.product.color,
                  productBrandReference:
                    certificateRequest.product.brandReference,
                }}
              />
            </Text>
          ) : null}
          {certificateRequest.purchaseOrder.product ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.purchaseOrder"
                components={{
                  purchaseOrder: <strong />,
                }}
                values={{
                  purchaseOrder: certificateRequest.purchaseOrder.product,
                }}
              />
            </Text>
          ) : null}
        </Group>
        <Group>
          {certificateRequest.componentName ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.component"
                values={{
                  componentName: certificateRequest.componentName,
                }}
                components={{
                  componentName: <strong />,
                }}
              />
            </Text>
          ) : null}
          {certificateRequest.materialName ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.material"
                values={{
                  materialName: certificateRequest.materialName,
                }}
                components={{
                  materialName: <strong />,
                }}
              />
            </Text>
          ) : null}
          {(certificateRequest.componentName ||
            certificateRequest.materialName) &&
          certificateRequest.purchaseOrder.operation ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.purchaseOrder"
                components={{
                  purchaseOrder: <strong />,
                }}
                values={{
                  purchaseOrder: certificateRequest.purchaseOrder.operation,
                }}
              />
            </Text>
          ) : null}
        </Group>
        <Checkbox
          checked={checked}
          onChange={(event) => onCheckboxChange?.(event.target.checked)}
          pos="absolute"
          left={0}
          top={0}
          m="xs"
        />
      </Stack>
    </Card>
  )
}
